import { useNavigate, useParams } from "react-router-dom";
import { useJobInfo } from "@/api/services/main/job";
import { useForm } from "react-hook-form";
import { TCaseBasicInfo } from "@/features/case/types/case";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCaseSchema } from "@/features/case/schemas/caseSchema";
import { useCaseInfo, useCaseMatches, updateCaseApi, deleteCaseMatchApi, updateCaseMatchApi } from "@/api/services/main/case";
import { TUpdateCaseRequestDto } from "@/api/services/main/case/dtos/update-case.dto";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { TGetCaseMatchesReqDto } from "@/api/services/main/case/dtos/get-case-matches.dto";
import { usePagination } from "@/hooks/usePagination";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { AppRoutes } from "@/routes/config";
import { useValidateId } from "@/hooks/useValidateId";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { getActiveStepFromCaseStatus, canEditCaseFromInfo, isCaseCompleted, isPersonInChargeLockedStatus } from "@/features/case/utils/caseStatusHelper";
import { getDateFieldKeysToClear } from "@/features/case/utils/caseMatchHelper";
import { normalizeString } from "@/utils/string";
import { fetchCheckCaseDuplicateApi } from "@/api/services/main/case";
import { TCheckCaseDuplicateDto } from "@/api/services/main/case/dtos/check-case-duplicate.dto";
import { useDialog } from "@/hooks/useDialog";

export const useCaseEditForm = () => {
  const { jobId, caseId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const jobQueryResult = useJobInfo(Number(jobId));
  const { data: jobInfoResponse } = jobQueryResult;
  const jobInfo = jobInfoResponse?.data ?? {};
  const [statusMap, setStatusMap] = useState<Record<number, number>>({});
  const [initialStatusMap, setInitialStatusMap] = useState<Record<number, number>>({});
  const [talentStatusMap, setTalentStatusMap] = useState<Record<number, number>>({});

  const caseQueryResult = useCaseInfo(Number(caseId));
  const { data: caseInfoResponse, isLoading: isCaseLoading, isError: isCaseError } = caseQueryResult;
  const caseInfo = caseInfoResponse?.data;

  // Case existence check: must complete before enabling other case-related APIs
  const isCaseCheckComplete = !isCaseLoading;
  const caseExists = isCaseCheckComplete && !isCaseError && !!caseInfo;

  // When the last match is deleted, BE soft-deletes the case (DEV-292). We redirect
  // to the job list, but the leave-page blocker would interrupt with either the
  // default or the custom "マッチング情報の入力が完了しておりません..." dialog if the form
  // is dirty or a status was changed. Setting this triggers the redirect from an
  // effect AFTER the page returns null and Form unmounts (useBlocker unregisters).
  const [isSoftDeleted, setIsSoftDeleted] = useState(false);
  const isSoftDeleting = isSoftDeleted;

  useEffect(() => {
    if (isSoftDeleted) {
      navigate(AppRoutes.jobList, { replace: true });
    }
  }, [isSoftDeleted, navigate]);

  const caseMatchesPagReq = usePagination();
  caseMatchesPagReq.perPage = 20;

  const [caseMatchFilterParams, setCaseMatchFilterParams] = useState<Partial<TGetCaseMatchesReqDto>>({});

  // Only fetch case matches after case existence is confirmed
  const caseMatchesQueryResult = useCaseMatches(Number(caseId), { ...caseMatchesPagReq, ...caseMatchFilterParams }, caseExists);
  const caseMatchesResponse = caseMatchesQueryResult.data;

  useValidateId("jobId", AppRoutes.caseList, jobQueryResult);

  // Redirect to job list if case doesn't exist (soft-deleted or invalid).
  // Covers all entry points to a deleted case: notification links, bookmarks,
  // dashboard offer-accepted links, etc. — they all land on this page.
  useEffect(() => {
    if (isCaseCheckComplete && !caseExists) {
      navigate(AppRoutes.jobList, { replace: true });
    }
  }, [isCaseCheckComplete, caseExists, navigate]);
  const activeStep = getActiveStepFromCaseStatus(caseInfo?.progressStatus);
  const { caseSchema } = useCaseSchema();
  const methods = useForm<TCaseBasicInfo>({
    defaultValues: {},
    resolver: yupResolver<TCaseBasicInfo>(caseSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const initialMemoRef = useRef<string | null | undefined>(caseInfo?.memo);
  const isSavingMemoRef = useRef(false);

  const [initialBasicInfo, setInitialBasicInfo] = useState<{
    name: string | undefined;
    managementNumber: string | undefined;
    personInChargeId: number | undefined;
  } | null>(null);

  useEffect(() => {
    if (caseInfo) {
      methods.reset({
        ...caseInfo,
        talentFilter: {},
      });

      if (caseInfo.memo !== undefined) {
        initialMemoRef.current = caseInfo.memo;
      }

      setInitialBasicInfo({
        name: caseInfo.name,
        managementNumber: caseInfo.managementNumber,
        personInChargeId: caseInfo.personInChargeId,
      });
    }
  }, [caseInfo, methods]);

  useEffect(() => {
    initialMemoRef.current = undefined;
    setInitialBasicInfo(null);
  }, [caseId]);

  const { watch } = methods;
  const managementNumber = watch("managementNumber");
  const name = watch("name");
  const personInChargeId = watch("personInChargeId");
  const caseMatchesData = caseMatchesResponse?.data ?? [];
  const isPersonInChargeLocked = isPersonInChargeLockedStatus(caseInfo?.progressStatus);

  // Duplicate case modal state
  const duplicateCaseModal = useDialog();
  const [duplicateCases, setDuplicateCases] = useState<TCheckCaseDuplicateDto[]>([]);
  const [pendingCaseName, setPendingCaseName] = useState<string>("");

  // Check if managementNumber passes validation (required, 6-digit number)
  const isBasicInfoValid = useMemo(() => {
    const trimmed = managementNumber?.trim();
    if (!trimmed) return false;
    return /^\d{6}$/.test(trimmed);
  }, [managementNumber]);

  const hasBasicInfoChanges = useMemo(() => {
    if (!initialBasicInfo) return false;
    if (!isBasicInfoValid) return false;
    const hasPersonInChargeChanged = !isPersonInChargeLocked && Number(personInChargeId) !== Number(initialBasicInfo.personInChargeId);
    return (
      normalizeString(name) !== normalizeString(initialBasicInfo.name) ||
      normalizeString(managementNumber) !== normalizeString(initialBasicInfo.managementNumber) ||
      hasPersonInChargeChanged
    );
  }, [name, managementNumber, personInChargeId, initialBasicInfo, isBasicInfoValid, isPersonInChargeLocked]);

  useEffect(() => {
    if (caseMatchesData.length > 0) {
      setInitialStatusMap((prev) => {
        const updated = { ...prev };
        caseMatchesData.forEach((caseMatch: TCaseMatches) => {
          if (caseMatch.id && !(caseMatch.id in prev)) {
            updated[caseMatch.id] = caseMatch.status ?? 0;
          }
        });
        return updated;
      });
      setStatusMap((prev) => {
        const updated = { ...prev };
        caseMatchesData.forEach((caseMatch: TCaseMatches) => {
          if (caseMatch.id && !(caseMatch.id in prev)) {
            updated[caseMatch.id] = caseMatch.status ?? 0;
          }
        });
        return updated;
      });
    }
  }, [caseMatchesData]);

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const page = caseMatchesResponse?.pagination?.page ?? 0;
  const perPage = caseMatchesResponse?.pagination?.perPage ?? 0;
  const count = caseMatchesResponse?.pagination?.count ?? 0;
  const countItem = count + perPage * page - perPage;
  const totalPage = caseMatchesResponse?.pagination?.totalPage ?? 1;
  const total = caseMatchesResponse?.pagination?.total ?? 0;

  const handleStatusMapChange = useCallback((newStatusMap: Record<number, number>) => {
    setStatusMap(newStatusMap);
  }, []);

  const handleTalentStatusMapChange = useCallback((newTalentStatusMap: Record<number, number>) => {
    setTalentStatusMap(newTalentStatusMap);
  }, []);

  const [caseMatchesWithMissingFieldsMap, setCaseMatchesWithMissingFieldsMap] = useState<Record<number, boolean>>({});

  const handleCaseMatchMissingFieldsUpdate = useCallback((updates: Record<number, boolean>) => {
    setCaseMatchesWithMissingFieldsMap((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const hasChangedStatusWithMissingFields = useMemo(() => {
    return Object.values(caseMatchesWithMissingFieldsMap).some((hasMissing) => hasMissing);
  }, [caseMatchesWithMissingFieldsMap]);

  const handleDeleteCaseMatch = useCallback(
    async (caseMatchId: number) => {
      if (!caseId) return;

      try {
        const response = await deleteCaseMatchApi(Number(caseId), caseMatchId);

        // Check if case was soft-deleted (last talent removed)
        if (response?.data?.caseDeleted) {
          toast.success(API_SUCCESS_MESSAGE);

          // Cancel any in-flight case queries to avoid a refetch racing the redirect.
          // Do NOT removeQueries here: while the hook is still mounted, removing the
          // cache forces a refetch which hits the now-deleted case and triggers the
          // global error toast ("ケースが存在しません。"). gcTime cleans up post-redirect.
          queryClient.cancelQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_LIST, Number(caseId)] });
          queryClient.cancelQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_MATCHES, Number(caseId)] });

          // Defer redirect through state so the page can unmount the Form
          // (and thereby the useBlocker) before navigation actually fires.
          // Prevents both the default and custom leave-page dialogs from appearing.
          setIsSoftDeleted(true);
          return;
        }

        // Normal deletion flow
        toast.success(API_SUCCESS_MESSAGE);

        // Remove from statusMap if exists
        setStatusMap((prev) => {
          const updated = { ...prev };
          delete updated[caseMatchId];
          return updated;
        });
        setInitialStatusMap((prev) => {
          const updated = { ...prev };
          delete updated[caseMatchId];
          return updated;
        });
        setCaseMatchesWithMissingFieldsMap((prev) => {
          const updated = { ...prev };
          delete updated[caseMatchId];
          return updated;
        });

        // Invalidate and refetch case info, case matches, and case schedules
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_MATCHES, Number(caseId)] }),
          queryClient.refetchQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULES, caseId] }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULE_DETAIL, caseId],
          }),
          caseQueryResult.refetch(),
          caseMatchesQueryResult.refetch(),
        ]);
        await caseQueryResult.refetch();
        await caseMatchesQueryResult.refetch();
      } catch (e) {
        showError(e);
      }
    },
    [caseId, queryClient, caseQueryResult, caseMatchesQueryResult, navigate],
  );

  const canEdit = canEditCaseFromInfo(caseInfo);
  const isCompleted = isCaseCompleted(caseInfo?.progressStatus);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingCaseMatchStatus, setIsSavingCaseMatchStatus] = useState(false);

  const hasStatusChanges = useMemo(() => {
    return Object.entries(statusMap).some(([id, status]) => {
      const initialStatus = initialStatusMap[Number(id)];
      return initialStatus !== undefined && Number(status) !== initialStatus;
    });
  }, [statusMap, initialStatusMap]);

  const onSubmit = async (data: TCaseBasicInfo) => {
    if (!caseId) return;
    if (!canEdit) return;
    if (isSubmitting) return;

    if (hasChangedStatusWithMissingFields) {
      toast.error("マッチング情報に未入力の必須項目があるため、ステータスを更新できません。");
      return;
    }

    setIsSubmitting(true);
    try {
      const changedCaseMatches = Object.entries(statusMap)
        .filter(([id, status]) => {
          const initialStatus = initialStatusMap[Number(id)];
          return initialStatus !== undefined && Number(status) !== initialStatus;
        })
        .map(([id, status]) => {
          const caseMatchUpdate: { id: number; status: number; talentStatus?: number } = {
            id: Number(id),
            status: Number(status),
          };

          if (talentStatusMap[Number(id)] !== undefined) {
            caseMatchUpdate.talentStatus = talentStatusMap[Number(id)];
          }

          return caseMatchUpdate;
        });

      const personInChargeId = Number(data.personInChargeId);
      if (!personInChargeId || personInChargeId === 0) {
        setIsSubmitting(false);
        return;
      }

      const updatePayload: TUpdateCaseRequestDto = {
        name: normalizeString(data.name) ?? "",
        managementNumber: normalizeString(data.managementNumber) ?? "",
        personInChargeId: personInChargeId,
        memo: normalizeString(data.memo),
        ...(changedCaseMatches.length > 0 && { caseMatches: changedCaseMatches }),
      };

      await updateCaseApi(Number(caseId), updatePayload);
      toast.success(API_SUCCESS_MESSAGE);

      await caseQueryResult.refetch();
      await caseMatchesQueryResult.refetch();

      setStatusMap({});
      setInitialStatusMap({});
      setTalentStatusMap({});
      setCaseMatchesWithMissingFieldsMap({});
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavigateBack = () => {
    navigate(AppRoutes.caseList);
  };

  const handleNavigateCreateCase = useCallback(() => {
    if (!jobId) return;
    const route = AppRoutes.caseCreate.replace(":jobId", jobId);
    navigate(route);
  }, [jobId, navigate]);

  // Auto save case match status when all missing fields are filled
  // newStatus parameter allows saving with a status that's not yet in state (for immediate save after status change)
  // newTalentStatus parameter is for CANDIDATE_WITHDRAWN status
  // previousStatus parameter is used to detect downgrade and clear affected date fields
  const onAutoSaveCaseMatchStatus = useCallback(
    async (caseMatchId: number, newStatus?: number, newTalentStatus?: number, previousStatus?: number) => {
      if (!caseId) return;
      if (!canEdit) return;

      // Use provided newStatus or fall back to statusMap
      const currentStatus = newStatus ?? statusMap[caseMatchId];
      const initialStatus = initialStatusMap[caseMatchId];

      // Only save if status has changed
      if (currentStatus === undefined || initialStatus === undefined || currentStatus === initialStatus) {
        return;
      }

      setIsSavingCaseMatchStatus(true);
      try {
        const caseMatchUpdate: { id: number; status: number; talentStatus?: number } = {
          id: Number(caseMatchId),
          status: Number(currentStatus),
        };

        // Use provided newTalentStatus or fall back to talentStatusMap
        const talentStatus = newTalentStatus ?? talentStatusMap[caseMatchId];
        if (talentStatus !== undefined) {
          caseMatchUpdate.talentStatus = talentStatus;
        }

        const data = methods.getValues();
        // Use server value when field is locked to prevent stale writes
        const personInChargeId = Number(isPersonInChargeLocked ? caseInfo?.personInChargeId : data.personInChargeId);
        if (!personInChargeId || personInChargeId === 0) {
          setIsSavingCaseMatchStatus(false);
          return;
        }

        const updatePayload: TUpdateCaseRequestDto = {
          name: normalizeString(data.name) ?? "",
          managementNumber: normalizeString(data.managementNumber) ?? "",
          personInChargeId: personInChargeId,
          memo: normalizeString(data.memo),
          caseMatches: [caseMatchUpdate],
        };

        // Step 1: Update status first
        await updateCaseApi(Number(caseId), updatePayload);

        // Step 2: Clear affected date fields on downgrade (after status is saved)
        const fromStatus = previousStatus ?? initialStatus;
        if (fromStatus > currentStatus) {
          const fieldsToClear = getDateFieldKeysToClear(fromStatus, currentStatus);
          if (fieldsToClear.length > 0) {
            const clearPayload: Record<string, null> = {};
            fieldsToClear.forEach((field) => {
              clearPayload[field] = null;
            });
            await updateCaseMatchApi(Number(caseId), caseMatchId, clearPayload);
          }
        }

        // Update initial status map to reflect saved status
        setInitialStatusMap((prev) => ({
          ...prev,
          [caseMatchId]: currentStatus,
        }));

        // Update statusMap to match saved status
        setStatusMap((prev) => ({
          ...prev,
          [caseMatchId]: currentStatus,
        }));

        // Clear missing fields flag for this case match
        setCaseMatchesWithMissingFieldsMap((prev) => {
          const updated = { ...prev };
          delete updated[caseMatchId];
          return updated;
        });

        await caseQueryResult.refetch();
        await caseMatchesQueryResult.refetch();
      } catch (e) {
        showError(e);
      } finally {
        setIsSavingCaseMatchStatus(false);
      }
    },
    [caseId, canEdit, isPersonInChargeLocked, caseInfo?.personInChargeId, statusMap, initialStatusMap, talentStatusMap, methods, caseQueryResult, caseMatchesQueryResult],
  );

  // Save only basic info (name, managementNumber, personInChargeId) - excludes memo and caseMatches
  const performSaveBasicInfo = async () => {
    if (!caseId) return;
    if (!canEdit) return;

    const data = methods.getValues();
    const currentPersonInChargeId = Number(data.personInChargeId);
    if (!currentPersonInChargeId || currentPersonInChargeId === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const updatePayload: TUpdateCaseRequestDto = {
        name: normalizeString(data.name) ?? "",
        managementNumber: normalizeString(data.managementNumber) ?? "",
        personInChargeId: currentPersonInChargeId,
      };

      await updateCaseApi(Number(caseId), updatePayload);
      toast.success(API_SUCCESS_MESSAGE);

      setInitialBasicInfo({
        name: data.name,
        managementNumber: data.managementNumber,
        personInChargeId: currentPersonInChargeId,
      });

      await caseQueryResult.refetch();
    } catch (e) {
      showError(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSaveBasicInfo = async () => {
    if (!caseId || !jobId) return;
    if (!canEdit) return;
    if (isSubmitting) return;

    const data = methods.getValues();
    const caseName = normalizeString(data.name);

    // Check duplicate case name if name has changed
    if (caseName && caseName !== normalizeString(initialBasicInfo?.name)) {
      try {
        const caseDuplicate = await fetchCheckCaseDuplicateApi(Number(jobId), caseName, Number(caseId));
        if (caseDuplicate?.data?.length > 0) {
          setDuplicateCases(caseDuplicate.data);
          setPendingCaseName(caseName);
          duplicateCaseModal.show();
          return;
        }
      } catch (e) {
        showError(e);
        return;
      }
    }

    await performSaveBasicInfo();
  };

  const onAutoSaveMemo = useCallback(
    async (newMemo: string | null | undefined) => {
      if (!caseId) return;
      if (!canEdit) return;
      if (isSavingMemoRef.current) return;

      const normalizedNewMemo = normalizeString(newMemo);
      const normalizedInitialMemo = normalizeString(initialMemoRef.current);

      if (normalizedNewMemo === normalizedInitialMemo) {
        return;
      }

      isSavingMemoRef.current = true;
      try {
        const data = methods.getValues();
        // Use server value when field is locked to prevent stale writes
        const personInChargeId = Number(isPersonInChargeLocked ? caseInfo?.personInChargeId : data.personInChargeId);

        if (!personInChargeId || personInChargeId === 0) {
          isSavingMemoRef.current = false;
          return;
        }

        const updatePayload: TUpdateCaseRequestDto = {
          name: normalizeString(data.name) ?? "",
          managementNumber: normalizeString(data.managementNumber) ?? "",
          personInChargeId: personInChargeId,
          memo: normalizedNewMemo,
        };

        await updateCaseApi(Number(caseId), updatePayload);
        toast.success(API_SUCCESS_MESSAGE);
        initialMemoRef.current = normalizedNewMemo;
        await caseQueryResult.refetch();
      } catch (e) {
        showError(e);
      } finally {
        isSavingMemoRef.current = false;
      }
    },
    [caseId, canEdit, isPersonInChargeLocked, caseInfo?.personInChargeId, methods, caseQueryResult],
  );

  return {
    // Form methods and state
    methods,
    caseSchema,
    onSubmit,
    hasStatusChanges,
    hasBasicInfoChanges,
    hasChangedStatusWithMissingFields,

    // Job info
    jobInfo,

    // Case info
    caseInfo,
    activeStep,
    managementNumber,
    caseMatchesData,
    total,
    countItem,
    totalPage,

    // Status handlers
    handleStatusMapChange,
    handleTalentStatusMapChange,
    handleDeleteCaseMatch,
    handleCaseMatchMissingFieldsUpdate,

    // Edit state
    canEdit,
    isCompleted,
    isPersonInChargeLocked,
    isSubmitting,
    isSavingCaseMatchStatus,

    // Utilities
    formatTotal,
    handleNavigateBack,
    handleNavigateCreateCase,
    onSaveBasicInfo,
    onAutoSaveCaseMatchStatus,
    onAutoSaveMemo,

    // Filter
    caseMatchFilterParams,
    setCaseMatchFilterParams,

    // Duplicate case modal
    duplicateCaseModal,
    duplicateCases,
    pendingCaseName,
    jobId,

    // Case existence check
    caseExists,

    // True while a soft-delete redirect is in flight — page should unmount the form
    isSoftDeleting,
  };
};
